import { replyFailureTexts, replyFailureView, shortRunId, shownDuration } from '@dbrain/app-core/reply-failure';
import type { TurnFailureDto } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDuration } from './Steps.js';

/**
 * 16b.1: the card of a turn that ended without an answer (`no_visible_reply`). The long-output case
 * (HataUzunDosya.dc.html, HataUzunDosyaAyrinti.dc.html) says what was cut and what to do; "Basit sürümle başla" writes
 * a suggested request into the box without sending it. The empty answer (HataBosCevap.dc.html) offers "Yeniden dene".
 * "Teknik ayrıntılar" opens what happened, the tries, the time and model and the run number with "Kopyala". The
 * words are app-core's, the phone's too. Only the conversation's latest card has the buttons that act.
 */
export function FailureCard({
  runId,
  failure,
  durationMs,
  model,
  profile,
  latest,
  onRetry,
  retrying,
  onSimple,
  empty = false,
}: {
  runId: string | null;
  failure: TurnFailureDto | null;
  /** 16c.1: the run ended with `empty_reply` (the generic card) */
  empty?: boolean;
  durationMs: number | null;
  model: string | null;
  profile: 'chat' | 'code';
  latest: boolean;
  onRetry?: () => void;
  retrying?: boolean;
  onSimple?: (text: string) => void;
}) {
  const { i18n } = useTranslation();
  const duration = useDuration();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const titleId = useId();
  const detailsId = useId();
  const words = replyFailureTexts(i18n.language);
  // 16c.1: no time under a second ("0 sn")
  const shown = shownDuration(durationMs);
  const view = replyFailureView({ failure, locale: i18n.language, profile, time: shown !== null ? duration.total(shown) : null, model, empty });
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () => {
    if (!runId) return;
    void navigator.clipboard?.writeText(runId).then(
      () => setCopied(true),
      () => undefined,
    );
  };
  return (
    <section className="reply-failure" role={latest ? 'alert' : undefined} aria-labelledby={titleId} data-testid="reply-failure" data-kind={view.kind}>
      <span className="reply-failure__icon" aria-hidden="true">
        <Icons.error size={18} />
      </span>
      <div className="reply-failure__body">
        <h2 id={titleId} className="reply-failure__title">
          {view.title}
        </h2>
        {view.body.map((text) => (
          <p key={text} className="reply-failure__text">
            {text}
          </p>
        ))}
        <p className="reply-failure__text">
          {view.advice.lead ? <strong>{view.advice.lead}</strong> : null}
          {view.advice.lead ? ' ' : null}
          {view.advice.text}
        </p>
        <div className="reply-failure__actions">
          {latest && view.simpleStart && onSimple ? (
            <Button className="reply-failure__action" icon={<Icons.skills size={15} />} onClick={() => onSimple(words.simplePrompt)} data-testid="reply-failure-simple">
              {words.actions.simple}
            </Button>
          ) : null}
          {latest && onRetry ? (
            <Button
              className="reply-failure__action"
              variant={view.simpleStart ? 'secondary' : 'primary'}
              icon={<Icons.regenerate size={15} />}
              busy={retrying}
              onClick={onRetry}
              data-testid="retry"
            >
              {words.actions.retry}
            </Button>
          ) : null}
          <span className="reply-failure__spacer" />
          <button type="button" className="reply-failure__toggle" aria-expanded={open} aria-controls={open ? detailsId : undefined} onClick={() => setOpen(!open)} data-testid="reply-failure-details-toggle">
            {words.actions.details}
            {open ? <Icons.chevronUp size={12} /> : <Icons.chevronDown size={12} />}
          </button>
        </div>
        {open ? (
          <dl id={detailsId} className="reply-failure__details" data-testid="reply-failure-details">
            {view.details.map((row) => (
              <div key={row.key} className="reply-failure__row" data-row={row.key}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
            {runId ? (
              <div className="reply-failure__row" data-row="run">
                <dt>{words.labels.run}</dt>
                <dd>
                  <code className="reply-failure__run" title={runId}>
                    {shortRunId(runId)}
                  </code>
                  <button type="button" className="reply-failure__copy" title={words.actions.copyLabel} onClick={copy} data-testid="reply-failure-copy">
                    {copied ? <Icons.check size={12} /> : <Icons.copy size={12} />}
                    <span aria-live="polite">{copied ? words.actions.copied : words.actions.copy}</span>
                  </button>
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>
    </section>
  );
}
